/**
 * External Imports
 */
import {
  Dispatch,
  FC,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo
} from 'react';
import { connect, useDispatch } from 'react-redux';
import { format } from 'date-fns';

/**
 * Internal Imports
 */
import CensusReportTable from '../../../components/customTables/reports/patient/censusReportTable';
import { RootState } from '../../../redux/store';
import {
  clearCensusReportFilter,
  getCensusReport,
  getCensusReportDownload,
  getCensusReportExport
} from '../../../redux/actions';
import { date } from '../../../utils/helpers';

/**
 * Global Type Definition Imports
 */
import {
  AuthUserProps,
  CensusReportList,
  Filter
} from '../../../config/interfaces';

/**
 * Style Imports
 */
import { CReportContainer } from '../styles/ReportsViews.styles';
import SearchHeader from '../../../components/searchHeader';

interface CensusReportProps {
  censusReport: CensusReportList[];
  authorizedUser: AuthUserProps;
  selectedUnits: { id: string; unit: string }[];
  searchTerm: string;
  censusReportFilter: Filter;
  currentCensusReportPage: number;
  sortKey: string;
  sortDir: string;
  reportSearchQuery: string;
  setSearchTerm: Dispatch<SetStateAction<string>>;
}

const CensusReport: FC<CensusReportProps> = ({
  censusReport,
  authorizedUser,
  selectedUnits,
  searchTerm,
  censusReportFilter,
  currentCensusReportPage,
  sortKey,
  sortDir,
  reportSearchQuery,
  setSearchTerm
}) => {
  const dispatch = useDispatch();
  const today = new Date();
  const todayDate = format(
    new Date(today.getFullYear(), today.getMonth(), today.getDate()),
    'yyyy-MM-dd'
  );

  useEffect(() => {
    return () => {
      dispatch(clearCensusReportFilter());
    };
  }, []);

  useEffect(() => {
    dispatch(
      getCensusReport({
        facilityId: authorizedUser.facilityId,
        unitId: selectedUnits.map(item => item.id),
        startday: date.formatFilterStartDate(
          censusReportFilter.startDate || todayDate
        ),
        endday: date.formatFilterEndDate(
          censusReportFilter.endDate || todayDate
        ),
        user34s: censusReportFilter.roundedBy || '',
        patient: reportSearchQuery || '',
        sortBy: sortKey,
        sortOrder: sortDir,
        page: currentCensusReportPage,
        limit: 50,
        status: censusReportFilter.status || ''
      })
    );
  }, [
    censusReportFilter,
    reportSearchQuery,
    authorizedUser?.facilityId,
    currentCensusReportPage,
    selectedUnits,
    sortDir,
    sortKey
  ]);

  const listToDisplay = useMemo(() => {
    if (censusReport?.length > 0) {
      const cleanData = censusReport.map((item: CensusReportList) => ({
        censusDate: date.formatDateDashtoSlash(item.reportDate),
        unit: item.unitName,
        unitId: item.unitId,
        room: item.room,
        patient: `${item.lastName}, ${item.firstName}`,
        mrn: item.mrn,
        lastRoundDate:
          item.rounds.length > 0
            ? `${date.dayOfYr(
                item.rounds[0]?.roundDate
              )} @ ${date.localTimeOfDay(item.rounds[0].roundDate)}`
            : '',
        lastRoundedBy:
          item.rounds.length > 0
            ? `${item.rounds[0].userFirstName} ${item.rounds[0].userLastName}`
            : '',
        lastRoundedById: item.rounds.length > 0 ? item.rounds[0].userId : '',
        status:
          item.status === 'none'
            ? 'Not Rounded'
            : item.status === 'rounded'
            ? 'Rounded'
            : 'Unable to Round'
      }));
      return cleanData;
    }
    return [];
  }, [censusReport, selectedUnits]);

  const handleExportCsv = useCallback(() => {
    dispatch(
      getCensusReportExport({
        facilityId: authorizedUser.facilityId,
        unitId: selectedUnits.map(item => item.id),
        startday: date.formatFilterStartDate(
          censusReportFilter.startDate || todayDate
        ),
        endday: date.formatFilterEndDate(
          censusReportFilter.endDate || todayDate
        ),
        user34s: censusReportFilter.roundedBy || '',
        patient: reportSearchQuery || '',
        sortBy: sortKey,
        sortOrder: sortDir,
        status: censusReportFilter.status || ''
      })
    );
  }, [
    authorizedUser,
    selectedUnits,
    censusReportFilter,
    reportSearchQuery,
    sortKey,
    sortDir
  ]);

  const handleClickDownloadPDF = useCallback(async () => {
    dispatch(
      getCensusReportDownload({
        facilityId: authorizedUser.facilityId,
        unitId: selectedUnits.map(item => item.id),
        startday: date.formatFilterStartDate(
          censusReportFilter.startDate || todayDate
        ),
        endday: date.formatFilterEndDate(
          censusReportFilter.endDate || todayDate
        ),
        user34s: censusReportFilter.roundedBy || '',
        patient: reportSearchQuery || '',
        sortBy: sortKey,
        sortOrder: sortDir,
        status: censusReportFilter.status || ''
      })
    );
  }, [
    authorizedUser,
    selectedUnits,
    censusReportFilter,
    reportSearchQuery,
    sortKey,
    sortDir
  ]);

  return (
    <CReportContainer id="Census-Report-Container">
      <SearchHeader
        searchTerm={searchTerm}
        setSearchTerm={setSearchTerm}
        handleExportCsv={handleExportCsv}
        handleClickDownloadPDF={handleClickDownloadPDF}
      />
      <CensusReportTable censusList={listToDisplay} />
    </CReportContainer>
  );
};

const mapReduxStateToProps = (state: RootState) => {
  return {
    authorizedUser: state.AuthorizedUser.authorizedUser,
    dataToDownload: state.AdminReducer.dataToDownload,
    censusReport: state.ReportReducer.censusReport,
    currentCensusReportPage: state.ReportReducer.currentCensusReportPage,
    censusReportFilter: state.ReportReducer.censusReportFilter,
    sortKey: state.ReportReducer.sortCensusKey,
    sortDir: state.ReportReducer.sortRoundsDir,
    reportSearchQuery: state.ReportReducer.reportSearchQuery,
    selectedUnits: state.ConfigReducer.selectedUnits,
    units: state.ConfigReducer.units
  };
};

export default connect(mapReduxStateToProps)(CensusReport);
